import { Injectable } from '@angular/core';
import * as THREE from 'three';
import { WorkbenchSelectionStore } from '../../../../../stores/workbench/workbench-selection.store';

@Injectable({
  providedIn: 'root'
})
export class WorkbenchSelectionManager {

  constructor(private readonly store: WorkbenchSelectionStore) {
    // Reactive Logic: React to raw intersections to determine hover/selection candidates?
    // Or leave that to explicit actions?
    // Usually, "Click" triggers selection logic, but "Hover" triggers cursor changes.

    // For now, we will expose methods to be called by interaction events (Click).
  }

  /**
   * Processes a click event to determine logical selection.
   * Logic:
   * 1. Check raw intersections from Store (or pass them in).
   * 2. Filter out technical objects (Gizmos, Helpers).
   * 3. Traverse up to find Entity (Plan/Group).
   * 4. Decide Group vs Leaf selection based on current state.
   */
  public handleSelection() {
    const intersections = this.store.rawIntersections();

    // 1. Find first valid Entity
    const validHit = intersections.find(hit => this.isSelectable(hit.object));

    if (validHit) {
      const entity = this.getLogicalEntity(validHit.object);
      if (entity) {
        const planId = entity.userData['id'];
        const groupId = entity.userData['groupId'];

        this.applySelection(planId, groupId);
      }
    } else {
      // Click on void -> Deselect
      this.store.reset();
    }
  }

  private isSelectable(obj: THREE.Object3D): boolean {
    // Traverse upwards to see if it belongs to a Plan
    let curr: THREE.Object3D | null = obj;
    while (curr) {
      if (curr.userData?.['plan']) return true;
      curr = curr.parent;
    }
    return false;
  }

  private getLogicalEntity(obj: THREE.Object3D): THREE.Object3D | null {
    let curr: THREE.Object3D | null = obj;
    while (curr) {
      if (curr.userData?.['plan']) return curr;
      curr = curr.parent;
    }
    return null;
  }

  private applySelection(planId: number, groupId: string | null) {
    // Logic for Group vs Item selection (Drill Down)
    // Current logic in legacy code: Always selects the GROUP (Plan Entity).
    // But if there is a 'groupId' (Assembly), logic might differ.

    // Legacy behavior: "All plans in a group are selected together" -> visualized by highlight.
    // But logically, `selectedObject` was the clicked Mesh/Group.

    // If clicking same object, do nothing or toggle?
    // Usually re-select is fine.
    this.store.selectPlan(planId);
    this.store.selectGroup(groupId || null);
  }
}
